/**
 * Authentication API endpoints
 * Supports: Keycloak password login, signup via backend, Google OAuth (PKCE)
 * Matches the portal project's authentication mechanism
 */
import { config } from '../env';
import { TokenResponse } from '@/types/auth';

// --- PKCE Helpers ---

function base64UrlEncode(array: Uint8Array): string {
  let str = '';
  for (const byte of array) {
    str += String.fromCharCode(byte);
  }
  return btoa(str)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

function generateCodeVerifier(): string {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return base64UrlEncode(array);
}

async function generateCodeChallenge(verifier: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(verifier);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return base64UrlEncode(new Uint8Array(digest));
}

// --- Safe JSON parse helper ---
async function safeParse(response: Response): Promise<any> {
  if (response.status === 204) return null;
  const contentType = response.headers.get('content-type') || '';
  if (contentType.includes('application/json')) {
    try {
      return await response.json();
    } catch {
      try {
        return await response.text();
      } catch {
        return null;
      }
    }
  }
  try {
    return await response.text();
  } catch {
    return null;
  }
}

export const authAPI = {
  /**
   * Login with Keycloak username and password (Resource Owner Password Grant)
   */
  login: async (username: string, password: string): Promise<TokenResponse> => {
    const params = new URLSearchParams({
      grant_type: 'password',
      client_id: config.keycloak.clientId,
      username,
      password,
      scope: 'openid profile email',
    });

    const response = await fetch(config.keycloak.tokenUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: params.toString(),
    });

    if (!response.ok) {
      const errorData = await safeParse(response);
      let errorMessage = 'Login failed';

      if (errorData && typeof errorData === 'object') {
        const errorDescription = errorData.error_description || '';
        const errorType = errorData.error || '';

        if (
          errorDescription.toLowerCase().includes('verify your email') ||
          errorDescription.toLowerCase().includes('email not verified')
        ) {
          errorMessage =
            'Email verification required. Please check your email inbox and click the verification link before logging in.';
        } else if (errorType === 'invalid_grant') {
          errorMessage = 'Invalid email or password. Please try again.';
        } else if (errorType === 'invalid_client') {
          errorMessage = 'Authentication client configuration error. Please contact support.';
        } else {
          errorMessage = errorDescription || errorMessage;
        }
      }

      throw new Error(errorMessage);
    }

    const data = await safeParse(response);
    if (!data || !data.access_token) {
      throw new Error('Invalid response from auth server: No access token received');
    }

    return data;
  },

  /**
   * Register a new user via the backend (creates user in Keycloak with role + sends verification email)
   */
  signup: async (
    email: string,
    password: string,
    role: string = 'CUSTOMER',
    firstName?: string,
    lastName?: string,
    phone?: string
  ): Promise<any> => {
    const registerPath = `${config.api.baseUrl}/api/users/register`;

    const response = await fetch(registerPath, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        email: email.trim().toLowerCase(),
        password,
        firstName: firstName?.trim() || '',
        lastName: lastName?.trim() || '',
        phone: phone?.trim() || '',
        ...(role ? { role } : {}),
      }),
    });

    if (!response.ok) {
      let errorMessage = 'Signup failed';
      const errorData = await safeParse(response);
      if (errorData && typeof errorData === 'object') {
        errorMessage = errorData.message || errorData.error || errorMessage;
      } else if (typeof errorData === 'string') {
        errorMessage = errorData;
      }
      throw new Error(errorMessage);
    }

    return safeParse(response);
  },

  /**
   * Initiate Google login via Keycloak IdP brokering with PKCE
   * Uses kc_idp_hint=google to skip Keycloak's login page
   */
  initiateGoogleLogin: async (): Promise<void> => {
    const redirectUri = window.location.origin + '/login';

    const codeVerifier = generateCodeVerifier();
    const codeChallenge = await generateCodeChallenge(codeVerifier);

    // Store verifier in localStorage (matches portal's approach)
    localStorage.setItem('pkce_code_verifier', codeVerifier);

    // Pass verifier via state parameter to survive redirect
    const state = codeVerifier;
    const loginUrl =
      `${config.keycloak.authUrl}?client_id=${config.keycloak.clientId}` +
      `&redirect_uri=${encodeURIComponent(redirectUri)}` +
      `&response_type=code` +
      `&scope=openid profile email` +
      `&kc_idp_hint=google` +
      `&code_challenge=${codeChallenge}` +
      `&code_challenge_method=S256` +
      `&state=${state}`;

    window.location.href = loginUrl;
  },

  /**
   * Exchange authorization code for tokens (OAuth callback)
   */
  exchangeCodeForToken: async (code: string): Promise<TokenResponse> => {
    const redirectUri = window.location.origin + '/login';
    const codeVerifier = localStorage.getItem('pkce_code_verifier');

    const bodyParams = new URLSearchParams({
      grant_type: 'authorization_code',
      client_id: config.keycloak.clientId,
      code,
      redirect_uri: redirectUri,
    });

    if (codeVerifier) {
      bodyParams.append('code_verifier', codeVerifier);
    }

    const response = await fetch(config.keycloak.tokenUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: bodyParams,
    });

    // Clean up verifier
    localStorage.removeItem('pkce_code_verifier');

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Token exchange failed:', errorText);
      throw new Error(`Token exchange failed: ${response.status}`);
    }

    const data = await safeParse(response);
    if (!data || !data.access_token) {
      throw new Error('No access token received');
    }

    return data;
  },

  /**
   * Refresh the access token using the stored refresh token
   */
  refreshToken: async (refreshToken: string): Promise<TokenResponse> => {
    const params = new URLSearchParams({
      grant_type: 'refresh_token',
      client_id: config.keycloak.clientId,
      refresh_token: refreshToken,
    });

    const response = await fetch(config.keycloak.tokenUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: params.toString(),
    });

    if (!response.ok) {
      throw new Error('Token refresh failed');
    }

    return safeParse(response);
  },

  /**
   * Logout — clear Keycloak session using refresh token
   */
  logout: async () => {
    const refreshToken = localStorage.getItem('refreshToken');

    if (refreshToken) {
      try {
        await fetch(config.keycloak.logoutUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: new URLSearchParams({
            client_id: config.keycloak.clientId,
            refresh_token: refreshToken,
          }),
        });
      } catch (err) {
        console.error('Keycloak logout failed', err);
      }
    }

    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
  },

  /**
   * Check if user has pending email verification
   */
  checkVerification: async (email: string): Promise<any> => {
    const response = await fetch(`${config.api.baseUrl}/api/users/check-verification`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: email.trim().toLowerCase() }),
    });
    if (response.ok) {
      return response.json();
    }
    return null;
  },

  /**
   * Trigger forgot password flow via backend (sends reset email through Keycloak)
   */
  forgotPassword: async (email: string): Promise<{ message: string }> => {
    const response = await fetch(`${config.api.baseUrl}/api/users/forgot-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: email.trim().toLowerCase() }),
    });

    const data = await safeParse(response);

    if (!response.ok) {
      throw new Error(data?.message || 'Failed to process password reset request');
    }

    return data;
  },
};

